import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Modal } from "react-bootstrap";
import moment from "moment";
import AllianceManagerServices from "../../../services/API/AllianceManager";
import { ROLE_NAME } from "../../../constant/roles";

const MIN_LENGTH = 3;
const MAX_LENGTH = 500;

// Saved row from the API -> table row (`key` keeps React keys stable for unsaved rows)
const toRow = (item) => ({
  key: `saved-${item.id}`,
  id: item.id,
  question: item.question || "",
  savedQuestion: item.question || "",
  answer: item.answer,
  answered_at: item.answered_at,
  created_at: item.created_at,
  requested_by: item.requested_by,
});

const createEmptyRow = () => ({
  key: `new-${Date.now()}-${Math.random()}`,
  id: null,
  question: "",
  answer: null,
});

const formatDate = (date) => (date ? moment(date).format("DD-MM-YY hh:mm A") : "--");

/**
 * Additional details requested from the end user, managed like the Signed Document List:
 * add rows -> Save (end user is mailed), edit / delete until the user answers. Answers are view only.
 */
const AdditionalDetailsList = ({ srnId, readonly }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Replaces saved rows with the API list, keeping any unsaved new rows below them
  const applySaved = (data, keepNewRows = false) => {
    const saved = (data || []).map(toRow);
    setRows((prev) => {
      const newRows = keepNewRows ? prev.filter((row) => !row.id) : [];
      const merged = [...saved, ...newRows];
      return merged.length || readonly ? merged : [createEmptyRow()];
    });
  };

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      const res = await AllianceManagerServices.getAdditionalDetails(srnId);
      if (res) applySaved(res.data);
      setLoading(false);
    };
    if (srnId) fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srnId]);

  const isEditable = (row) => !readonly && !row.answer;
  const hasChanges = rows.some(
    (row) => !row.id || (isEditable(row) && row.question.trim() !== row.savedQuestion)
  );
  const savedRows = rows.filter((row) => row.id);
  const answeredCount = savedRows.filter((row) => row.answer).length;

  const updateQuestion = (key, question) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, question } : row)));

  const removeNewRow = (key) => setRows((prev) => prev.filter((row) => row.key !== key));

  const validateRows = () => {
    const seen = new Set();
    for (let i = 0; i < rows.length; i++) {
      const question = rows[i].question.trim();
      if (!question) return `Row ${i + 1}: Detail name is required`;
      if (question.length < MIN_LENGTH)
        return `Row ${i + 1}: Detail name should be at least ${MIN_LENGTH} characters`;
      if (seen.has(question.toLowerCase()))
        return `Row ${i + 1}: Duplicate detail "${question}"`;
      seen.add(question.toLowerCase());
    }
    return "";
  };

  const handleSave = async () => {
    const error = validateRows();
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    const res = await AllianceManagerServices.saveAdditionalDetails({
      srn_id: srnId,
      details: rows.map((row) => ({ id: row.id, question: row.question.trim() })),
    });
    if (res) {
      toast.success("Additional details saved successfully");
      applySaved(res.data);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    setDeletingId(id);
    const res = await AllianceManagerServices.deleteAdditionalDetail(id);
    if (res) {
      toast.success("Additional detail removed successfully");
      applySaved(res.data, true);
    }
    setDeletingId(null);
  };

  // Nothing requested and nothing can be added (closed SRN): hide the card
  if (!loading && readonly && !rows.length) return null;

  return (
    <div className="card card-sm card-shadow mt-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="heading_light m-0">
            <i
              className="fa fa-copy fa-fw"
              aria-hidden="true"
              style={{ color: "#64308e" }}
              title="Additional Details List"
            ></i>
            {" "}Additional Details List
            {savedRows.length > 0 && (
              <span className="sdl-pill-badge">
                <span className="sdl-pill-text">
                  {answeredCount} / {savedRows.length} Details Answered
                </span>
              </span>
            )}
          </h3>
          {!readonly && (
            <button
              className="button_new m-2"
              style={{ cursor: "pointer" }}
              onClick={() => setRows((prev) => [...prev, createEmptyRow()])}
            >
              <i className="fa-solid fa-plus" style={{ color: "green" }}></i>
              &nbsp;Add
            </button>
          )}
        </div>

        <div className="table-responsive">
          <table className="table table-bordered mt-3">
            <thead>
              <tr>
                <th width="5%">S.No</th>
                <th width="25%">Details Required</th>
                <th width="25%">User Response</th>
                <th width="15%">Requested By</th>
                <th width="15%">Status</th>
                <th width="8%">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.key}>
                    <td>{index + 1}</td>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. GST Number"
                        maxLength={MAX_LENGTH}
                        value={row.question}
                        disabled={!isEditable(row)}
                        onChange={(e) => updateQuestion(row.key, e.target.value)}
                      />
                    </td>
                    <td className="text-break">
                      {row.answer ?? "--"}
                      {row.answered_at && (
                        <small className="d-block text-muted">{formatDate(row.answered_at)}</small>
                      )}
                    </td>
                    <td>
                      {/* User name with the role name below it */}
                      {row.requested_by?.name || "--"}
                      {ROLE_NAME[row.requested_by?.user_role_id] && (
                        <small className="d-block text-muted">
                          {ROLE_NAME[row.requested_by.user_role_id]}
                        </small>
                      )}
                    </td>
                    <td>
                      {row.id && (
                        <span className="small">
                          <i
                            className={`fa fa-circle fa-fw ${row.answer ? "text-success" : "text-warning"}`}
                            aria-hidden="true"
                          ></i>{" "}
                          {row.answer ? "Answered" : "Awaiting user response"}
                        </span>
                      )}
                    </td>
                    <td className="text-center align-middle">
                      {!row.id ? (
                        <button
                          className="button_new"
                          style={{ cursor: "pointer", border: "none" }}
                          title="Delete"
                          onClick={() => removeNewRow(row.key)}
                        >
                          <i className="fa-solid fa-trash" style={{ color: "red", fontSize: "15px" }}></i>
                        </button>
                      ) : isEditable(row) ? (
                        <button
                          className="button_new"
                          style={{ cursor: "pointer", border: "none" }}
                          title="Delete"
                          disabled={deletingId === row.id}
                          onClick={() => setDeleteConfirmId(row.id)}
                        >
                          {deletingId === row.id ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="fa-solid fa-trash" style={{ color: "red", fontSize: "15px" }}></i>
                          )}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!readonly && (
          <div className="text-end">
            <button
              className="btn button_new mt-2"
              style={{
                background: hasChanges ? "green" : "#aaa",
                color: "white",
                cursor: hasChanges ? "pointer" : "not-allowed",
              }}
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? "Loading..." : "Save"}
            </button>
          </div>
        )}
      </div>

      <Modal show={!!deleteConfirmId} onHide={() => setDeleteConfirmId(null)} animation={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Do you want to delete this detail?</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setDeleteConfirmId(null)}>
            No
          </Button>
          <Button variant="success" onClick={handleDelete}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdditionalDetailsList;
